import { Check, X, CircleAlert, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency, formatDate } from "@/lib/format";
import { ReviewStatus } from "./CampaignClientsTable";
import type { SavingsCoffee, SavingsProspect } from "./types";

const pounds = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value);
export function buyingPattern(coffee: SavingsCoffee): string {
  const coverage = `${coffee.purchaseCount} of ${coffee.completedOrderCount} completed orders`;
  const interval =
    coffee.averageDaysBetweenPurchases === null
      ? "One purchase"
      : `About every ${Math.max(1, Math.round(coffee.averageDaysBetweenPurchases))} days`;
  return `${coffee.everyOrder ? "Every completed order" : interval} · ${coverage} · ${coffee.activeMonths} active months`;
}

export function CampaignClientReview({
  prospect,
  disclaimer,
  onSave,
  saving,
  saved,
}: {
  prospect: SavingsProspect;
  disclaimer: string;
  onSave?: () => void;
  saving?: boolean;
  saved?: boolean;
}) {
  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <h2 className="text-xl font-semibold tracking-tight text-balance">
          {prospect.customerName}
        </h2>
        <p className="text-sm text-muted-foreground">
          {prospect.email || "Email required"}
        </p>
        <div className="flex flex-wrap gap-2">
          <ReviewStatus prospect={prospect} />
          <Badge variant="secondary">
            {prospect.salesRepName
              ? `Rep: ${prospect.salesRepName}`
              : "Rep unassigned"}
          </Badge>
          <Badge variant="secondary">
            {prospect.marketingConsent
              ? "Marketing consent recorded"
              : "Consent missing"}
          </Badge>
        </div>
      </div>
      {prospect.reviewReasons.length > 0 && (
        <Card role="status" className="border-amber-600/20 bg-amber-500/5 py-0">
          <CardContent className="space-y-2 p-4">
            <CircleAlert
              className="size-4 text-amber-700 dark:text-amber-400"
              aria-hidden="true"
            />
            <p className="text-sm font-medium">Resolve before outreach</p>
            <div className="text-sm text-muted-foreground">
              <ul className="list-disc space-y-1 pl-4">
                {prospect.reviewReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
      <Tabs defaultValue="analysis">
        <TabsList>
          <TabsTrigger value="analysis">Purchase analysis</TabsTrigger>
          <TabsTrigger value="message">Message draft</TabsTrigger>
        </TabsList>
        <TabsContent value="analysis" className="space-y-4 pt-2">
          {prospect.coffees.length === 0 ? (
            <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
              No completed coffee purchases in this period. Try a longer
              purchase window.
            </div>
          ) : (
            prospect.coffees.map((coffee, index) => (
              <section
                key={`${coffee.productId}-${coffee.historicalCargoId ?? index}`}
                className="rounded-lg border"
              >
                <div className="border-b bg-muted/20 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">#{index + 1} by weight</Badge>
                    <h3 className="font-medium">{coffee.productName}</h3>
                  </div>
                  {coffee.proposedProductName && (
                    <p className="mt-2 text-sm">
                      Propose:{" "}
                      <span className="font-medium">
                        {coffee.proposedProductName}
                      </span>
                      {coffee.proposedOciNumber && (
                        <span className="mt-1 block text-xs text-muted-foreground">
                          Proposed OCI: {coffee.proposedOciNumber}
                        </span>
                      )}
                      {coffee.proposedCargoStatus &&
                        coffee.proposedCargoStatus !== "in_spot" && (
                          <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
                            Incoming
                            {coffee.proposedArrivalDate !== undefined
                              ? ` · Expected ${formatDate(coffee.proposedArrivalDate)}`
                              : " · Arrival to confirm"}
                          </span>
                        )}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {buyingPattern(coffee)}
                  </p>
                </div>
                <dl className="grid grid-cols-2 gap-x-5 gap-y-4 p-4 sm:grid-cols-3">
                  {[
                    ["Purchased", `${pounds(coffee.purchasedPounds)} lb`],
                    [
                      "Average paid / lb",
                      formatCurrency(coffee.averagePricePerPound),
                    ],
                    ["3-month demand", `${pounds(coffee.projectedPounds)} lb`],
                    [
                      "Commitment",
                      coffee.contractPounds === null
                        ? "—"
                        : `${pounds(coffee.contractPounds)} lb (${coffee.contractBags} bags)`,
                    ],
                    [
                      "Contract / lb",
                      coffee.contractPricePerPound === null
                        ? "—"
                        : formatCurrency(coffee.contractPricePerPound),
                    ],
                    [
                      "Estimated savings",
                      coffee.estimatedSavings === null
                        ? "—"
                        : formatCurrency(coffee.estimatedSavings),
                    ],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-xs text-muted-foreground">{label}</dt>
                      <dd className="mt-1 text-sm font-medium tabular-nums">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
                {coffee.historicalCost !== null &&
                  coffee.contractCost !== null && (
                    <p className="border-t px-4 py-3 text-xs text-muted-foreground">
                      For the same commitment weight:{" "}
                      {formatCurrency(coffee.historicalCost)} at historical
                      pricing → {formatCurrency(coffee.contractCost)} on
                      contract.
                    </p>
                  )}
                {coffee.reviewReason && (
                  <p className="border-t px-4 py-3 text-sm text-amber-800 dark:text-amber-400">
                    {coffee.reviewReason}
                  </p>
                )}
              </section>
            ))
          )}
          {prospect.coffees.length > 0 && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              Three-month demand uses average monthly pounds. Commitments round
              up to whole bags with a one-bag minimum for campaign planning.
              Savings compare the same weight and cover coffee pricing only.
              Final contract and release terms require confirmation.
            </p>
          )}
        </TabsContent>
        <TabsContent value="message" className="pt-2">
          {prospect.eligible && prospect.estimatedSavings !== null ? (
            <div className="rounded-lg border">
              <div className="flex items-center justify-between gap-3 border-b bg-muted/20 px-4 py-3">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="size-4" aria-hidden="true" />
                  Email preview
                </span>
                <Badge variant="secondary">Draft · Not sent</Badge>
              </div>
              <div className="space-y-4 p-4">
                <p className="text-sm">
                  <span className="text-muted-foreground">Subject: </span>Could
                  contracting your coffee save you{" "}
                  {formatCurrency(prospect.estimatedSavings)}?
                </p>
                <p className="text-sm leading-relaxed">
                  Interested in saving an estimated{" "}
                  <strong>{formatCurrency(prospect.estimatedSavings)}</strong>{" "}
                  on{" "}
                  {prospect.coffees
                    .map((c) => c.proposedProductName ?? c.productName)
                    .join(" and ")}{" "}
                  by contracting your coffee?
                </p>
                <p className="text-sm text-muted-foreground">
                  Based on three months of estimated demand, rounded to contract
                  bag increments. {prospect.salesRepName} can walk you through
                  the options.
                </p>
                {prospect.coffees.some(
                  (coffee) =>
                    coffee.proposedProductId &&
                    coffee.proposedProductId !== coffee.productId,
                ) && (
                  <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                    This comparison proposes replacement coffees based on your
                    historical volume. Flavor, quality, and suitability need a
                    separate review with your sales representative.
                  </p>
                )}
                <div
                  className="flex flex-wrap gap-2"
                  aria-label="Response buttons preview"
                >
                  <Button disabled>
                    <Check aria-hidden="true" />
                    Yes, I’m interested
                  </Button>
                  <Button variant="outline" disabled>
                    <X aria-hidden="true" />
                    No, thanks
                  </Button>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {disclaimer}
                </p>
                <p className="text-xs text-muted-foreground">
                  Preview only. Response buttons are inactive and no email is
                  sent.
                </p>
              </div>
            </div>
          ) : prospect.marketingConsent &&
            prospect.reviewReasons.length === 0 &&
            prospect.coffees.length > 0 &&
            prospect.coffees.every(
              (coffee) =>
                coffee.contractPounds !== null &&
                coffee.proposedProductName &&
                (coffee.reviewReason === null ||
                  coffee.reviewReason.startsWith("Incoming coffee:")),
            ) ? (
            <div className="rounded-xl border bg-muted/20 p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Rotation message preview
                </p>
                <Badge variant="outline">
                  Draft · Arrival confirmation required
                </Badge>
              </div>
              <p className="mt-5 text-sm">
                We’ve reviewed your coffee purchases and would like to discuss
                these options for your next rotation:
              </p>
              <ul className="mt-3 space-y-3 text-sm">
                {prospect.coffees.map((coffee, index) => (
                  <li key={`${coffee.productId}-${index}`}>
                    Based on your purchases of {coffee.productName}, consider{" "}
                    <strong>{coffee.proposedProductName}</strong> for an
                    estimated {pounds(coffee.contractPounds!)} lb (
                    {coffee.contractBags} bags) over three months.
                    {coffee.proposedCargoStatus !== "in_spot" && (
                      <span className="mt-1 block text-muted-foreground">
                        Incoming coffee
                        {coffee.proposedArrivalDate !== undefined
                          ? `, expected ${formatDate(coffee.proposedArrivalDate)}`
                          : ", arrival date to confirm"}
                        . Availability and timing require confirmation.
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-muted-foreground">
                {prospect.salesRepName} can help review flavor, quality,
                suitability, and final contract terms.
              </p>
              <p className="mt-4 text-xs text-muted-foreground">{disclaimer}</p>
              <p className="mt-3 text-xs text-muted-foreground">
                Review required before outreach. No email or notification is
                sent.
              </p>
            </div>
          ) : (
            <p className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">
              A savings message will appear once purchase history, pricing,
              consent and assignment checks pass.
            </p>
          )}
        </TabsContent>
      </Tabs>
      {onSave && (
        <div className="flex items-center justify-end border-t pt-4">
          <Button onClick={onSave} disabled={saving}>
            {saving
              ? "Saving client…"
              : saved
                ? "Refresh saved estimate"
                : "Add client to draft"}
          </Button>
        </div>
      )}
    </div>
  );
}
